/**
 * 关联业务「查看详情」弹框：各 Tab 固定 label 与金蝶/接口常见字段名（多 key 兜底）
 */

export type FieldDef = { label: string; keys: string[] }

export type TableColDef = { title: string; keys: string[] }

export function pickRowValue(row: Record<string, unknown>, keys: string[]): unknown {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') return row[k]
  }
  const lowerKeys = new Map(Object.keys(row).map((k) => [k.toLowerCase(), k]))
  for (const k of keys) {
    const lk = k.toLowerCase()
    const actual = lowerKeys.get(lk)
    if (actual) {
      const v = row[actual]
      if (v !== undefined && v !== null && String(v).trim() !== '') return v
    }
  }
  return undefined
}

/** 合同 — 基本信息 */
export const CONTRACT_BASIC_FIELDS: FieldDef[] = [
  { label: '单据编号', keys: ['bill_no', 'billNo', 'documentNo', 'document_no', 'fbillno'] },
  { label: '单据类型', keys: ['bill_type_id_name', 'bill_type_id_fname', 'billTypeName', 'bill_type_name'] },
  { label: '合同名称', keys: ['name', 'contract_name', 'f_vsay_text', 'contractName', 'fname'] },
  { label: '销售组织', keys: ['saleorgid_name', 'sale_org_id_name', 'saleorgid_fname', 'saleOrgName'] },
  { label: '客户', keys: ['cust_id_name', 'cust_id_fname', 'cust_name', 'customer_name', 'partyB'] },
  { label: '日期', keys: ['date', 'biz_date', 'bizDate', 'create_date', 'fdate'] },
  { label: '联系人名称', keys: ['contact_name', 'contactName', 'linkman', 'link_man', 'contacts', 'contact_id_name'] },
  { label: '销售部门', keys: ['saledeptid_name', 'sale_dept_id_name', 'saleDeptName', 'saledeptid_fname'] },
  { label: '结算币别', keys: ['settle_curr_id_name', 'settle_cur_id_name', 'settleCurIdName', 'cur_id_name', 'currency', 'currency_name'] },
  { label: '销售部', keys: ['salegroupid_name', 'sale_group_id_name', 'saleGroupName', 'salegroupid_fname'] },
  { label: '有效起始日期', keys: ['period_start', 'periodStart', 'validfrom', 'valid_from', 'contract_start', 'eff_start_date'] },
  { label: '有效截止日期', keys: ['period_end', 'periodEnd', 'close_date', 'validto', 'valid_to', 'contract_end', 'delivery_date'] },
  { label: '销售员', keys: ['saler_id_name', 'salerIdName', 'salesperson_id_name', 'salesman_name', 'fsalerid_name'] },
  { label: '合同有效期(天)', keys: ['f_period_validity', 'validity_days', 'contract_period_days', 'period_days'] },
  { label: '合同标识', keys: ['contract_tag', 'contractTag', 'contract_type_name', 'contract_identifier', 'f_contract_type'] },
  { label: '单据状态', keys: ['document_status_name', 'documentStatusName', 'document_status', 'documentStatus', 'bill_status', 'billStatus', 'fdocumentstatus'] },
  { label: '合同编号', keys: ['no', 'contract_no', 'contract_number', 'f_number', 'contractNo', 'contractno'] },
  { label: '合同毛利', keys: ['contract_gross_profit', 'gross_profit', 'f_contract_profit', 'contractProfit'] },
  { label: '申请人', keys: ['applicant_id_name', 'applicantIdName', 'creator_name', 'creator', 'apply_name', 'fapplicantid_name'] },
  { label: '客户名称', keys: ['customer_name', 'cust_name', 'fcust_name', 'cust_display_name', 'fcustomername'] },
  { label: '毛利率%', keys: ['gross_margin_rate', 'profit_rate', 'margin_pct', 'gross_margin', 'fmarginrate'] },
  { label: '用章事由', keys: ['f_vsay_remarks', 'f_vsay_remark', 'seal_reason', 'use_seal_reason', 'chop_reason', 'reason_for_seal', 'sealReason', 'fusealreason'] },
  { label: '合同总金额', keys: ['all_amount', 'contract_amount', 'amount', 'total_amount', 'price_tax_total', 'fallamount'] },
  { label: '价格条款', keys: ['price_clause', 'price_terms', 'price_term_id_name', 'pricelist_clause', 'fpricelistclause'] },
]

/** 合同 — 财务信息 */
export const CONTRACT_FINANCE_FIELDS: FieldDef[] = [
  { label: '保证金比例（%）', keys: ['deposit_ratio', 'margin_rate', 'fdepositratio', 'bond_rate'] },
  {
    label: '总金额',
    keys: [
      'bill_all_amount',
      'contract_amount',
      'f_vsay_amount',
      'all_amount',
      'total_amount',
      'amount',
      'fallamount',
      'ftotalamount',
    ],
  },
  { label: '结算方式', keys: ['settle_type_id_name', 'settleTypeName', 'settle_mode', 'fsettletypeid_name'] },
  { label: '收款条件', keys: ['rec_condition_id_name', 'rec_condition_id_fname', 'receive_condition', 'frecconditionid_name'] },
  { label: '是否含税', keys: ['is_tax', 'is_include_tax', 'tax_included', 'fistax', 'ispriceexcludetax', 'is_price_exclude_tax'] },
  { label: '价外税', keys: ['tax_out_price', 'price_out_tax', 'fispriceouttax'] },
  { label: '价目表', keys: ['pricelist_id_name', 'pricelistIdName', 'price_list_id_name', 'fpricelistid_name'] },
  { label: '折扣表', keys: ['discount_id_name', 'discountlist_id_name', 'fdiscountlistid_name'] },
  { label: '结算币别', keys: ['settle_curr_id_name', 'settle_cur_id_name', 'cur_id_name', 'currency_name', 'fsettlecurid_name'] },
  { label: '总金额(本位币)', keys: ['bill_tax_amount_lc', 'local_total_amount', 'all_amount_lc', 'amount_local', 'flocalamount'] },
  { label: '保证金', keys: ['deposit', 'margin', 'bond_amount', 'fdeposit'] },
  { label: '本位币', keys: ['local_curr_id_name', 'base_currency_id_name', 'local_cur_name', 'fbasecurid_name'] },
  { label: '汇率类型', keys: ['exchange_type_id_name', 'exchange_rate_type', 'exchangeratetype_id_name', 'fexratetype_name'] },
  { label: '汇率', keys: ['exchange_rate', 'exchangerate', 'fexchangerate'] },
  { label: '关联保证金', keys: ['related_deposit', 'link_margin', 'frelateddeposit'] },
  { label: '关联退款保证金', keys: ['related_refund_deposit', 'link_refund_margin', 'frelatedrefunddeposit'] },
]

/** 合同 — 合同明细表头 */
export const CONTRACT_LINE_COLUMNS: TableColDef[] = [
  { title: '序号', keys: ['seq', 'row_no', 'rowno', 'fseq', 'entry_seq', 'line_no'] },
  { title: '物料编码', keys: ['material_id_number', 'material_number', 'materialno', 'fmaterialid_number'] },
  { title: '物料名称', keys: ['material_id_name', 'material_fname', 'material_name', 'materialname', 'fmaterialid_name'] },
  { title: '规格型号', keys: ['specification', 'spec', 'material_model', 'fspecification'] },
  { title: 'BOM版本', keys: ['bom_version', 'bom_ver', 'fbomversion'] },
  {
    title: '销售单位',
    keys: ['sale_unit_id_name', 'stock_unit_id_name', 'unit_id_name', 'base_unit_id_name', 'sal_unit_name', 'fsaleunitid_name'],
  },
  { title: '销售数量', keys: ['qty', 'sale_qty', 'fsaleqty', 'sales_qty'] },
  { title: '计价单位', keys: ['price_unit_id_name', 'unit_id_name', 'stock_unit_id_name', 'price_unit_name', 'fpriceunitid_name'] },
  { title: '计价数量', keys: ['price_qty', 'qty', 'fpriceqty', 'priceqty'] },
  { title: '单价', keys: ['price', 'fprice', 'unit_price'] },
  { title: '含税单价', keys: ['tax_price', 'taxprice', 'ftaxprice'] },
  { title: '税率%', keys: ['entry_tax_rate', 'tax_rate', 'taxrate', 'ftaxrate'] },
  { title: '金额', keys: ['amount', 'famount', 'no_tax_amount'] },
  { title: '税额', keys: ['entry_tax_amount', 'tax_amount', 'taxamount', 'ftaxamount', 'tax_amount_lc'] },
  { title: '价税合计', keys: ['all_amount', 'tax_all_amount', 'fallamount', 'ftaxallamount'] },
  { title: '要货日期', keys: ['delivery_date', 'require_date', 'need_date', 'fdeliverydate'] },
  { title: '结算组织备注', keys: ['sett_org_remarks', 'settle_org_remark', 'fsettorgremark'] },
]

/** 订单 — 基本信息 */
export const ORDER_BASIC_FIELDS: FieldDef[] = [
  { label: '单据类型', keys: ['bill_type_id_name', 'bill_type_id_fname', 'billTypeName'] },
  { label: '销售组织', keys: ['saleorgid_name', 'sale_org_id_name', 'saleorgid_fname'] },
  { label: '销售部门', keys: ['saledeptid_name', 'sale_dept_id_name'] },
  { label: '合同编号', keys: ['contract_no', 'contract_number', 'src_bill_no', 'fcontractno'] },
  { label: '单据编号', keys: ['bill_no', 'billNo', 'documentNo', 'fbillno'] },
  { label: '客户', keys: ['cust_id_name', 'cust_id_fname', 'customer_name'] },
  { label: '销售组', keys: ['salegroupid_name', 'sale_group_id_name'] },
  { label: '价格条款', keys: ['price_clause', 'price_terms', 'price_term_id_name', 'fpricelistclause'] },
  { label: '日期', keys: ['date', 'biz_date', 'create_date', 'fdate'] },
  {
    label: '结算币别',
    keys: ['settle_curr_id_name', 'cur_id_name', 'settle_cur_id_name', 'currency_name', 'fsettlecurid_name'],
  },
  { label: '本位币', keys: ['local_curr_id_name', 'base_currency_id_name', 'fbasecurid_name'] },
  { label: '库存组织', keys: ['stock_org_id_name', 'stock_org_id_fname', 'fstockorgid_name'] },
  { label: '销售员', keys: ['saler_id_name', 'saler_id_fname', 'salesperson_id_name', 'fsalerid_name'] },
  { label: '业务类型', keys: ['business_type', 'biz_type_name', 'fbiztypeid_name'] },
  { label: '价目表', keys: ['pricelist_id_name', 'price_list_id_name', 'fpricelistid_name'] },
  { label: '单据状态', keys: ['document_status_name', 'documentStatusName', 'document_status', 'documentStatus', 'bill_status'] },
  { label: '汇率类型', keys: ['exchange_type_id_name', 'exchange_rate_type', 'fexratetype_name'] },
  { label: '交货方式', keys: ['delivery_way', 'delivery_method', 'deliverymode', 'fdeliveryway'] },
  { label: '收款条件', keys: ['rec_condition_id_name', 'rec_condition_id_fname', 'frecconditionid_name'] },
  { label: '变更原因', keys: ['change_reason', 'changereason', 'fchangereason'] },
  { label: '交货地点', keys: ['receive_address', 'delivery_place', 'delivery_address', 'fdeliveryplace'] },
  { label: '联系人', keys: ['link_man', 'link_phone', 'contact_name', 'contactName', 'linkman'] },
  { label: '整单折扣额', keys: ['bill_discount', 'whole_discount', 'fdiscountamount'] },
  { label: '备注', keys: ['note', 'remark', 'comments', 'fnote'] },
  { label: '运输方式', keys: ['transport_mode', 'carry_mode', 'ftransportmode'] },
  { label: '是否含税', keys: ['is_included_tax', 'isincludedtax', 'is_tax', 'is_include_tax', 'fistax'] },
  { label: '关联标签', keys: ['f_vsay_text', 'f_vsay_combo', 'f_vsay_combo1'] },
]

/** 订单 — 财务信息 */
export const ORDER_FINANCE_FIELDS: FieldDef[] = [
  { label: '结算币别', keys: ['settle_curr_id_name', 'cur_id_name', 'settle_cur_id_name', 'fsettlecurid_name'] },
  { label: '折扣表', keys: ['discount_id_name', 'fdiscountlistid_name'] },
  { label: '本位币', keys: ['local_curr_id_name', 'base_currency_id_name', 'fbasecurid_name'] },
  { label: '未收款金额', keys: ['un_receive_amount', 'unrecamount', 'funreceiveamount'] },
  { label: '收款条件', keys: ['rec_condition_id_name', 'rec_condition_id_fname', 'frecconditionid_name'] },
  { label: '税额', keys: ['tax_amount', 'taxamount', 'ftaxamount'] },
  { label: '汇率类型', keys: ['exchange_type_id_name', 'exchange_rate_type', 'fexratetype_name'] },
  { label: '累计收款金额', keys: ['total_rec_amount', 'sum_rec_amount', 'ftotalrecamount'] },
  { label: '结算方式', keys: ['settle_type_id_name', 'fsettletypeid_name'] },
  { label: '金额', keys: ['amount', 'famount'] },
  { label: '汇率', keys: ['exchange_rate', 'exchangerate', 'fexchangerate'] },
  { label: '累计退款金额', keys: ['total_refund_amount', 'sum_refund_amount', 'ftotalrefundamount'] },
  { label: '价目表', keys: ['pricelist_id_name', 'price_list_id_name', 'fpricelistid_name'] },
  { label: '价税合计', keys: ['bill_all_amount', 'all_amount', 'tax_all_amount', 'fallamount'] },
  { label: '预收金额', keys: ['rec_advance_amount', 'advance_rec_amount', 'frecadvanceamount'] },
  { label: '是否含税', keys: ['is_included_tax', 'isincludedtax', 'is_tax', 'is_include_tax', 'fistax'] },
  { label: '价外税', keys: ['tax_out_price', 'fispriceouttax'] },
  { label: '整单折扣额', keys: ['bill_discount', 'fdiscountamount'] },
  { label: '保证金比例（%）', keys: ['deposit_ratio', 'fdepositratio'] },
  { label: '关联保证金', keys: ['related_deposit', 'frelateddeposit'] },
  { label: '保证金', keys: ['deposit', 'fdeposit'] },
  { label: '关联退款保证金', keys: ['related_refund_deposit', 'frelatedrefunddeposit'] },
  { label: '要货订单编号', keys: ['require_order_no', 'need_order_no', 'freqorderno'] },
]

/** 订单 — 明细表头 */
export const ORDER_LINE_COLUMNS: TableColDef[] = [
  { title: '序号', keys: ['seq', 'row_no', 'fseq'] },
  { title: '物料编码', keys: ['material_id_number', 'material_number', 'fmaterialid_number'] },
  { title: '物料名称', keys: ['material_id_name', 'material_id_fname', 'material_name', 'fmaterialid_name'] },
  { title: '规格型号', keys: ['specification', 'spec', 'fspecification'] },
  {
    title: '销售单位',
    keys: ['unit_id_name', 'unit_id_fname', 'sale_unit_id_name', 'stock_unit_id_name', 'fsaleunitid_name'],
  },
  { title: '销售数量', keys: ['stock_qty', 'qty', 'base_unit_qty', 'sale_qty', 'fsaleqty'] },
  { title: '计价数量', keys: ['priceunitqty', 'price_qty', 'fpriceqty'] },
  { title: '单价', keys: ['price', 'fprice', 'unit_price'] },
  { title: '含税单价', keys: ['tax_price', 'taxprice', 'ftaxprice'] },
  { title: '税率%', keys: ['entry_tax_rate', 'tax_rate', 'ftaxrate'] },
  { title: '税额', keys: ['tax_amount', 'entry_tax_amount', 'ftaxamount'] },
  { title: '金额', keys: ['amount', 'famount', 'no_tax_amount'] },
  { title: '是否赠品', keys: ['is_free', 'isgift', 'fgift'] },
  { title: '价税合计', keys: ['all_amount', 'fallamount', 'tax_all_amount'] },
  {
    title: '要货日期',
    keys: ['delivery_date', 'min_plan_delivery_date', 'plan_date', 'fdeliverydate'],
  },
  { title: '库存组织', keys: ['stock_org_id_name', 'fstockorgid_name'] },
  { title: '结算组织', keys: ['sett_org_id_name', 'settle_org_id_name', 'fsettorgid_name'] },
  { title: '货主类型', keys: ['owner_type', 'fownertype'] },
  { title: '货主', keys: ['owner_id_name', 'fownerid_name'] },
  { title: '备注', keys: ['note', 'remark', 'entry_note', 'fnote'] },
]

/** 收款 — 基本信息 */
export const RECEIPT_BASIC_FIELDS: FieldDef[] = [
  { label: '单据类型', keys: ['bill_type_id_name', 'bill_type_id_fname'] },
  { label: '付款单位类型', keys: ['pay_unit_type', 'payer_type', 'contactunittype', 'fpayunittype'] },
  { label: '收款组织', keys: ['payorgid_name', 'rec_org_id_name', 'receive_org_id_name', 'frecorgid_name'] },
  { label: '合同编号', keys: ['contract_no', 'contract_number', 'fcontractno'] },
  { label: '单据编号', keys: ['bill_no', 'billNo', 'fbillno'] },
  { label: '付款单位', keys: ['pay_unit_id_name', 'payer_id_name', 'fpayunitid_name'] },
  { label: '结算组织', keys: ['settleorgid_name', 'sett_org_id_name', 'settle_org_id_name', 'fsettorgid_name'] },
  { label: '币别', keys: ['currency_id_name', 'cur_id_name', 'currency_name', 'fcurid_name'] },
  { label: '销售组织', keys: ['saleorgid_name', 'sale_org_id_name'] },
  { label: '业务日期', keys: ['date', 'biz_date', 'fdate'] },
  { label: '往来单位类型', keys: ['contact_unit_type', 'contactunittype', 'fcontactunittype'] },
  { label: '应收金额', keys: ['rec_amount', 'receivable_amount', 'frecamount'] },
  { label: '销售部门', keys: ['saledeptid_name', 'sale_dept_id_name'] },
  { label: '销售组', keys: ['salegroupid_name', 'sale_group_id_name'] },
  { label: '往来单位', keys: ['contact_unit_id_name', 'contact_id_name', 'fcontactunitid_name'] },
  { label: '实收金额', keys: ['real_rec_amount', 'factrecamount'] },
  { label: '销售员', keys: ['saler_id_name', 'fsalerid_name'] },
  { label: '销售订单号', keys: ['saleorderno', 'sale_order_no', 'src_bill_no', 'fsaleorderno'] },
  { label: '关联标签', keys: ['f_vsay_text'] },
  { label: '单据状态', keys: ['document_status_name', 'documentStatusName', 'document_status', 'documentStatus', 'bill_status', 'billStatus', 'fdocumentstatus'] },
  { label: '收款账户', keys: ['accountid_name', 'account_id_name', 'bank_account_name', 'fbankaccount'] },
  { label: '备注', keys: ['note', 'remark', 'fnote'] },
]

/** 收款 — 明细表头 */
export const RECEIPT_LINE_COLUMNS: TableColDef[] = [
  { title: '序号', keys: ['seq', 'row_no', 'fseq'] },
  { title: '结算方式', keys: ['settle_type_id_name', 'fsettletypeid_name'] },
  { title: '收款用途', keys: ['rec_purpose', 'purpose_name', 'fpurposeid_name'] },
  { title: '预收项目类型', keys: ['pre_item_type', 'fpreitemtype'] },
  { title: '预收销售订单', keys: ['pre_sale_order', 'fpresaleorder'] },
  { title: '应收金额', keys: ['rec_amount', 'frecamount'] },
  { title: '现金折扣', keys: ['cash_discount', 'fcashdiscount'] },
  { title: '长短款', keys: ['over_short', 'fovershort'] },
  { title: '我方银行账号', keys: ['accountid_number', 'bank_account', 'our_bank_account', 'fbankaccount'] },
  { title: '开户银行', keys: ['accountid_name', 'bank_name', 'fbankname'] },
  { title: '现金账号', keys: ['cash_account', 'fcashaccount'] },
  { title: '结算号', keys: ['settle_no', 'fsettleno'] },
  { title: '备注', keys: ['note', 'remark', 'fnote'] },
  { title: '物料编码', keys: ['material_id_number', 'fmaterialid_number'] },
  { title: '物料名称', keys: ['material_id_name', 'fmaterialid_name'] },
  { title: '销售订单号', keys: ['sale_order_no', 'saleorderno', 'src_bill_no', 'fsaleorderno'] },
  { title: '含税单价', keys: ['tax_price', 'price', 'ftaxprice'] },
  { title: '订单行号', keys: ['orderentryid', 'order_entry_seq', 'sale_order_entry', 'forderentryseq'] },
  { title: '数量', keys: ['qty', 'quantity', 'fqty'] },
  { title: '费用项目', keys: ['cost_item_id_name', 'fcostitemid_name'] },
  { title: '费用承担部门', keys: ['cost_dept_id_name', 'fcostdeptid_name'] },
  { title: '实收金额', keys: ['real_rec_amount', 'factrecamount'] },
  { title: '折后金额', keys: ['discount_amount', 'fdiscountamount'] },
  { title: '勾对', keys: ['check_flag', 'fcheckflag'] },
  { title: '预估税率(%)', keys: ['estimate_tax_rate', 'festimatetaxrate'] },
  { title: '税额本位币', keys: ['tax_amount_lc', 'ftaxamount_lc'] },
  { title: '应收金额不含税', keys: ['rec_amount_excl_tax', 'frecamountexcltax'] },
  { title: '应收金额不含税本位币', keys: ['rec_amount_excl_tax_lc', 'frecamountexcltaxlc'] },
]

/** 物流 — 基本信息 */
export const LOGISTICS_BASIC_FIELDS: FieldDef[] = [
  { label: '单据类型', keys: ['bill_type_id_name', 'bill_type_id_fname'] },
  { label: '客户', keys: ['customer_id_name', 'cust_id_name', 'receiver_id_name', 'cust_id_fname'] },
  { label: '发货组织', keys: ['send_org_id_name', 'delivery_org_id_name', 'fsendorgid_name', 'stock_org_id_name'] },
  { label: '签收地点', keys: ['sign_place', 'sign_address', 'fsignplace'] },
  { label: '单据编号', keys: ['bill_no', 'billNo', 'fbillno'] },
  { label: '销售订单号', keys: ['order_no', 'src_bill_no', 'sale_order_no', 'fsaleorderno'] },
  { label: '交货地点', keys: ['receive_address', 'delivery_place', 'delivery_address', 'fdeliveryplace'] },
  { label: '发货部门', keys: ['sale_dept_id_name', 'send_dept_id_name', 'delivery_dept_id_name', 'fsenddeptid_name'] },
  { label: '签收时间', keys: ['sign_time', 'sign_date', 'fsigntime'] },
  { label: '日期', keys: ['date', 'biz_date', 'delivery_date', 'fdate'] },
  { label: '承运商', keys: ['carrier', 'carrier_name', 'fcarrierid_name'] },
  { label: '库存组', keys: ['stock_group_id_name', 'fstockgroupid_name'] },
  { label: '签收人', keys: ['signer', 'sign_user_name', 'fsigner'] },
  { label: '结算币别', keys: ['cur_id_name', 'settle_cur_id_name'] },
  { label: '运输单号', keys: ['carriage_no', 'tracking_no', 'trackingNo', 'waybill_no', 'ftransportno'] },
  { label: '仓管员', keys: ['stocker_id_name', 'keeper_name', 'fstockerid_name'] },
  { label: '签收联系方式', keys: ['link_phone', 'sign_contact', 'sign_phone', 'fsigncontact'] },
  { label: '销售组织', keys: ['saleorgid_name', 'sale_org_id_name'] },
  { label: '销售组', keys: ['salegroupid_name', 'sale_group_id_name'] },
  { label: '单据状态', keys: ['document_status_name', 'documentStatusName', 'document_status', 'stock_status_id_name', 'bill_status'] },
  { label: '销售部门', keys: ['saledeptid_name', 'sale_dept_id_name'] },
  { label: '销售员', keys: ['saler_id_name', 'fsalerid_name'] },
  { label: '汇率类型', keys: ['exchange_type_id_name', 'exchange_rate_type', 'fexratetype_name'] },
  { label: '汇率', keys: ['exchange_rate', 'exchangerate', 'fexchangerate'] },
  { label: '备注', keys: ['note', 'remark', 'fnote'] },
]

/** 物流 — 明细列表 */
export const LOGISTICS_LINE_COLUMNS: TableColDef[] = [
  { title: '序号', keys: ['seq', 'row_no', 'fseq'] },
  { title: '物料编码', keys: ['material_id_number', 'fmaterialid_number'] },
  { title: '物料名称', keys: ['material_id_name', 'material_name', 'fmaterialid_name'] },
  { title: '规格型号', keys: ['specification', 'fspecification'] },
  { title: '库存单位', keys: ['unit_id_name', 'base_unit_id_name', 'stock_unit_id_name', 'fstockunitid_name'] },
  { title: '应发数量', keys: ['priceunitqty', 'must_qty', 'plan_qty', 'qty', 'fmustqty'] },
  { title: '实发数量', keys: ['real_qty', 'stock_qty', 'act_qty', 'fqty'] },
  { title: '是否赠品', keys: ['is_free', 'fgift'] },
  { title: '批号', keys: ['lot_name', 'lot_number', 'lot', 'flot'] },
  { title: '保质期单位', keys: ['exp_unit', 'expiry_unit', 'fexpunit'] },
  { title: '生产日期', keys: ['produce_date', 'production_date', 'fproducedate'] },
  { title: '有效期至', keys: ['expiry_date', 'expire_date', 'fexpirydate'] },
  { title: '保质期', keys: ['exp_period', 'shelf_life', 'fexpperiod'] },
  { title: '仓库', keys: ['stock_id_name', 'warehouse_name', 'fstockid_name'] },
  { title: '仓位', keys: ['stock_loc_id_name', 'location_name', 'fstocklocid_name'] },
  { title: '库存状态', keys: ['stock_status_id_name', 'fstockstatusid_name'] },
  { title: '质量类型', keys: ['quality_type', 'fqualitytype'] },
  { title: '计划跟踪号', keys: ['plan_track_no', 'mto_no', 'fplantrackno'] },
  { title: '备注', keys: ['note', 'remark', 'fnote'] },
  { title: '单价', keys: ['price', 'fprice'] },
  { title: '含税单价', keys: ['tax_price', 'ftaxprice'] },
  { title: '税率%', keys: ['entry_tax_rate', 'tax_rate', 'ftaxrate'] },
  { title: '金额', keys: ['amount', 'famount'] },
  { title: '价税合计', keys: ['all_amount', 'fallamount'] },
  { title: '签收数量', keys: ['sign_qty', 'receive_qty', 'fsignqty'] },
  { title: 'VMI业务', keys: ['vmi_flag', 'is_vmi', 'fvmibusiness'] },
]

/** 物流 — 物料数据分组（取首行分录） */
export const LOGISTICS_MATERIAL_GROUPS: { title: string; fields: FieldDef[] }[] = [
  {
    title: '物料信息',
    fields: [
      { label: '物料编码', keys: ['material_id_number', 'fmaterialid_number'] },
      { label: '物料名称', keys: ['material_id_name', 'material_name', 'fmaterialid_name'] },
    ],
  },
  {
    title: '数量信息',
    fields: [
      { label: '库存单位', keys: ['unit_id_name', 'base_unit_id_name', 'stock_unit_id_name', 'fstockunitid_name'] },
      { label: '实发数量', keys: ['real_qty', 'stock_qty', 'act_qty', 'fqty'] },
      { label: '销售单位', keys: ['sale_unit_id_name', 'unit_id_name', 'fsaleunitid_name'] },
      { label: '销售数量', keys: ['sale_qty', 'qty', 'fsaleqty'] },
      { label: '计价单位', keys: ['price_unit_id_name', 'unit_id_name', 'fpriceunitid_name'] },
      { label: '计价数量', keys: ['priceunitqty', 'price_qty', 'fpriceqty'] },
      { label: '辅单位', keys: ['sec_unit_id_name', 'fsecunitid_name'] },
      { label: '数量（辅单位）', keys: ['sec_qty', 'fsecqty'] },
    ],
  },
  {
    title: '其他信息',
    fields: [
      { label: 'BOM 版本', keys: ['bom_version', 'fbomversion'] },
      { label: '辅助属性', keys: ['aux_prop_id_name', 'fauxpropid_name'] },
      { label: '物料类别', keys: ['material_category', 'fcategoryid_name'] },
      { label: '货主类型', keys: ['owner_type', 'fownertype'] },
      { label: '货主', keys: ['owner_id_name', 'fownerid_name'] },
      { label: '保管者类型', keys: ['keeper_type', 'fkeepertypeid_name'] },
      { label: '保管者', keys: ['keeper_id_name', 'fkeeperid_name'] },
      { label: 'VMI 业务', keys: ['vmi_flag', 'fvmibusiness'] },
    ],
  },
  {
    title: '批号保质期',
    fields: [
      { label: '批号', keys: ['lot_name', 'lot_number', 'flot'] },
      { label: '生产日期', keys: ['produce_date', 'fproducedate'] },
      { label: '保质期', keys: ['exp_period', 'fexpperiod'] },
      { label: '保质期单位', keys: ['exp_unit', 'fexpunit'] },
      { label: '有效期至', keys: ['expiry_date', 'fexpirydate'] },
    ],
  },
]

/** 发票 — 基本信息 */
export const INVOICE_BASIC_FIELDS: FieldDef[] = [
  { label: '单据类型', keys: ['bill_type_id_name', 'bill_type_id_fname'] },
  { label: '客户', keys: ['customer_id_name', 'cust_id_name', 'cust_id_fname'] },
  { label: '销售员', keys: ['saler_id_name', 'fsalerid_name'] },
  { label: '立账类型', keys: ['account_type', 'build_account_type', 'faccounttype'] },
  { label: '发票类型', keys: ['invoice_type_name', 'invoiceType', 'finvoicetype'] },
  { label: '单据编号', keys: ['bill_no', 'billNo', 'fbillno'] },
  { label: '币别', keys: ['currency_id_name', 'cur_id_name', 'currency_name', 'fcurid_name'] },
  {
    label: '结算组织',
    keys: ['settleorgid_name', 'sett_org_id_name', 'settle_org_id_name', 'fsettorgid_name'],
  },
  { label: '收款组织', keys: ['payorgid_name', 'rec_org_id_name', 'receive_org_id_name', 'frecorgid_name'] },
  { label: '开票理由', keys: ['invoice_reason', 'open_reason', 'finvoicereason'] },
  { label: '发票号码', keys: ['f_vsay_text3', 'invoice_no', 'invoiceNo', 'f_invoice_number', 'number'] },
  { label: '业务日期', keys: ['biz_date', 'date', 'invoice_date', 'fbizdate'] },
  { label: '价税合计', keys: ['f_vsay_amount', 'all_amount', 'tax_all_amount', 'total_amount', 'fallamount'] },
  { label: '销售订单号', keys: ['ordernumber', 'sale_order_no', 'saleorderno', 'fsaleorderno'] },
  { label: '到期日', keys: ['due_date', 'expire_date', 'fduedate'] },
  { label: '收款条件', keys: ['rec_condition_id_name', 'rec_condition_id_fname', 'frecconditionid_name'] },
  { label: '销售组织', keys: ['saleorgid_name', 'sale_org_id_name'] },
  { label: '单据状态', keys: ['document_status_name', 'documentStatusName', 'document_status', 'bill_status'] },
  { label: '销售部门', keys: ['saledeptid_name', 'sale_dept_id_name'] },
  { label: '整单折扣金额', keys: ['bill_discount', 'whole_discount', 'fdiscountamount'] },
  { label: '申请人', keys: ['applicant_id_name', 'creator_name', 'fapplicantid_name'] },
  { label: '源单编号', keys: ['source_bill_no', 'contract_no', 'src_bill_no', 'fcontractno'] },
  { label: '价外税', keys: ['tax_out_price', 'fispriceouttax'] },
  { label: '按含税单价录入', keys: ['is_tax_price', 'tax_price_entry', 'fistaxprice'] },
  { label: '参与暂估应收核销', keys: ['join_est_ar', 'festarwriteoff'] },
  { label: '先到票后出库', keys: ['invoice_before_out', 'finvoicebeforeout'] },
  { label: '关联标签', keys: ['f_vsay_text'] },
  { label: '库存组织', keys: ['stock_org_id_name', 'fstockorgid_name'] },
]

/** 发票 — 明细表头 */
export const INVOICE_LINE_COLUMNS: TableColDef[] = [
  { title: '序号', keys: ['seq', 'row_no', 'fseq'] },
  { title: '物料编码', keys: ['material_id_number', 'fmaterialid_number'] },
  { title: '物料名称', keys: ['material_id_name', 'fmaterialid_name'] },
  { title: '计价单位', keys: ['price_unit_id_name', 'fpriceunitid_name'] },
  { title: '计价数量', keys: ['price_qty', 'priceunitqty', 'fpriceqty'] },
  { title: '含税单价', keys: ['tax_price', 'ftaxprice'] },
  { title: '单价', keys: ['price', 'fprice'] },
  { title: '税率 (%)', keys: ['entry_tax_rate', 'tax_rate', 'ftaxrate'] },
  { title: '折扣率 (%)', keys: ['discount_rate', 'fdiscountrate'] },
  { title: '不含税金额', keys: ['no_tax_amount', 'amount', 'famount'] },
  { title: '税额', keys: ['tax_amount', 'tax_amount_for', 'entry_tax_amount', 'ftaxamount'] },
  { title: '折扣额', keys: ['discount_amount', 'fdiscount'] },
  { title: '价税合计', keys: ['all_amount', 'fallamount', 'tax_all_amount'] },
  { title: '应收金额', keys: ['receive_amount', 'rec_amount', 'pay_amount'] },
  { title: '核销状态', keys: ['written_off_status', 'write_off_status', 'fwriteoffstatus'] },
  { title: '备注', keys: ['remark', 'note', 'fnote'] },
  { title: '计价基本数量', keys: ['base_price_qty', 'fbasepriceqty'] },
  { title: '库存单位', keys: ['stock_unit_id_name', 'fstockunitid_name'] },
  { title: '库存数量', keys: ['stock_qty', 'fstockqty'] },
  { title: '是否赠品', keys: ['is_free', 'fgift'] },
  { title: '库存基本数量', keys: ['base_stock_qty', 'fbasestockqty'] },
  { title: '销售单位', keys: ['sale_unit_id_name', 'fsaleunitid_name'] },
  { title: '销售数量', keys: ['sale_qty', 'fsaleqty'] },
  { title: '销售基本数量', keys: ['base_sale_qty', 'fbasesaleqty'] },
  { title: '成本金额', keys: ['cost_amount', 'fcostamount'] },
  { title: '销售订单行号', keys: ['orderentryid', 'sale_order_entry_seq', 'fsaleorderentryseq'] },
  { title: '销售基本数量 (财务)', keys: ['base_sale_qty_fin', 'fbasesaleqtyfin'] },
  { title: '已结算金额', keys: ['settled_amount', 'fsettledamount'] },
]

/** 发票 — 收款计划表头 */
export const INVOICE_PLAN_COLUMNS: TableColDef[] = [
  { title: '序号', keys: ['seq', 'row_no', 'fseq'] },
  { title: '到期日', keys: ['due_date', 'fduedate'] },
  { title: '应收比例 (%)', keys: ['rec_rate', 'rec_percent', 'frecrate'] },
  { title: '应收金额', keys: ['rec_amount', 'frecamount'] },
  { title: '应收金额本位币', keys: ['rec_amount_lc', 'frecamount_lc'] },
  { title: '销售订单号', keys: ['sale_order_no', 'saleorderno', 'fsaleorderno'] },
  { title: '订单行号', keys: ['order_entry_seq', 'forderentryseq'] },
  { title: '物料编码', keys: ['material_id_number', 'fmaterialid_number'] },
  { title: '物料名称', keys: ['material_id_name', 'fmaterialid_name'] },
  { title: '备注', keys: ['note', 'remark', 'fnote'] },
]
